import { makeAutoObservable } from 'mobx';
import { A2UI_EVENT_NAMES } from '../../../../vendor/agentplace-a2ui/event-names.ts';
import {
  AGUI_CUSTOM_EVENT_NAMES,
  type AguiEvent,
} from '../../../../vendor/agent-library/agui/events.ts';
import { ContentType, type AgentContent } from '../../../../vendor/agent-library/types/content.ts';
import { isRecord } from '../util/type-guards.ts';
import { PROCESS_NARRATIONS } from './process/narration.ts';
import { authoredNarration, messageNarration, type NarrationLine } from './narration-line.ts';

export type ActivityState = 'preparing' | 'running' | 'returned' | 'failed' | 'unconfirmed';

export interface ToolActivity {
  toolCallId: string;
  kind: 'surface' | 'research' | 'tool' | 'search' | 'image' | 'service' | 'computation';
  state: ActivityState;
  narration?: NarrationLine;
  commentary?: NarrationLine;
  subjects?: string[];
  sources?: { url: string; title: string }[];
  imageUrl?: string;
  surfaceId?: string;
  /** Accepted by the client surface store, not a browser-paint or business-success receipt. */
  surfaceApplied?: boolean;
}

export interface RunActivity {
  responseId: string;
  runId?: string;
  parentRunId?: string;
  state: 'running' | 'finished' | 'failed' | 'stopped' | 'unconfirmed';
  tools: ToolActivity[];
  truncated: boolean;
}

const MAX_RUNS = 20;
const MAX_TOOLS = 100;

/** Session-only facts from the native event stream, not synthesized content cleanup or tool names. */
export class NarrationStore {
  readonly runs = new Map<string, RunActivity>();
  responseId: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get current(): RunActivity | undefined {
    return this.responseId ? this.runs.get(this.responseId) : undefined;
  }

  get line(): NarrationLine | null {
    const run = this.current;
    if (!run || run.state !== 'running') {
      return null;
    }
    const active = run.tools.findLast(
      (tool) => tool.state === 'preparing' || tool.state === 'running',
    );
    if (active?.narration) {
      return active.narration;
    }
    if (active?.kind === 'surface') {
      return messageNarration('screenReady');
    }
    if (active?.kind === 'search' || active?.kind === 'research') {
      return messageNarration('lookingUp');
    }
    if (active?.kind === 'image' || active?.kind === 'service' || active?.kind === 'computation') {
      return messageNarration(active.kind);
    }
    if (active?.state === 'preparing') {
      return messageNarration('preparingTool');
    }
    return messageNarration('working');
  }

  /** Optional public context for the same operation as the working line. */
  get detail(): NarrationLine | null {
    const run = this.current;
    if (run?.state !== 'running') {
      return null;
    }
    const tool =
      run.tools.findLast((item) => item.state === 'preparing' || item.state === 'running') ??
      run.tools.at(-1);
    if (!tool || tool.state === 'failed' || tool.state === 'unconfirmed') {
      return null;
    }
    if (!tool.narration && tool.commentary) {
      return tool.commentary;
    }
    if (tool.sources?.length) {
      const hosts = [...new Set(tool.sources.map((source) => new URL(source.url).hostname))];
      return authoredNarration(hosts.slice(0, 2).join(', '));
    }
    const subject = tool.subjects?.at(-1);
    return !tool.narration && subject ? authoredNarration(subject) : null;
  }

  reset(): void {
    this.runs.clear();
    this.responseId = null;
  }

  handle(event: AguiEvent, responseId: string): void {
    if (event.type === 'RUN_STARTED') {
      const run = this.#run(responseId, event.parentRunId);
      if (run.state !== 'running') {
        return;
      }
      run.runId = event.runId;
      this.responseId = responseId;
      return;
    }
    if (event.type === 'RUN_FINISHED' || event.type === 'RUN_ERROR') {
      const run = this.runs.get(responseId);
      if (!run || (run.state !== 'running' && run.state !== 'unconfirmed')) {
        return;
      }
      if (event.type === 'RUN_ERROR') {
        run.state = 'failed';
      } else if (event.x?.status === 'aborted') {
        run.state = 'stopped';
      } else {
        run.state = 'finished';
      }
      this.#settleUnknown(run);
      return;
    }
    if (event.type === 'TOOL_CALL_START' || event.type === 'TOOL_CALL_END') {
      this.#observe(
        responseId,
        event.toolCallId,
        'preparing',
        event.x?.componentName,
        event.x?.activityKind,
      );
    } else if (event.type === 'TOOL_CALL_RESULT') {
      this.#observe(
        responseId,
        event.toolCallId,
        event.x?.isError ? 'failed' : 'returned',
        event.x?.componentName,
        event.x?.activityKind,
      );
      const tool = this.runs
        .get(responseId)
        ?.tools.find((item) => item.toolCallId === event.toolCallId);
      if (tool && !event.x?.isError && isRecord(event.x?.props)) {
        this.#details(tool, event.x.componentName, event.x.props);
      }
      if (tool && !event.x?.isError) {
        this.#progress(tool, event.x?.progress, false);
      }
    } else if (
      event.type === 'CUSTOM' &&
      event.name === AGUI_CUSTOM_EVENT_NAMES.toolPhase &&
      isRecord(event.value) &&
      event.value.state === 'output-pending' &&
      typeof event.value.toolCallId === 'string'
    ) {
      this.#observe(
        responseId,
        event.value.toolCallId,
        'running',
        event.value.componentName,
        event.value.activityKind,
      );
      const run = this.runs.get(responseId);
      const toolCallId = event.value.toolCallId;
      const tool = run?.tools.find((item) => item.toolCallId === toolCallId);
      if (tool && run?.state === 'running') {
        if (isRecord(event.value.props)) {
          this.#details(tool, event.value.componentName, event.value.props);
        }
        this.#progress(tool, event.value.progress, true);
      }
    }
  }

  /** Transport loss is not a successful operation or evidence that server execution stopped. */
  disconnect(): void {
    for (const run of this.runs.values()) {
      if (run.state === 'running') {
        run.state = 'unconfirmed';
        this.#settleUnknown(run);
      }
    }
  }

  /** Called only after the existing surface reducer accepts the event. Replay creates no activity. */
  handleAppliedSurface(name: string, value: unknown, responseId?: string): void {
    if (
      !responseId ||
      name !== A2UI_EVENT_NAMES.updateComponents ||
      !isRecord(value) ||
      value.previewToolCallId !== undefined ||
      typeof value.toolCallId !== 'string' ||
      typeof value.surfaceId !== 'string' ||
      !Array.isArray(value.components) ||
      !value.components.some(
        (node) => isRecord(node) && node.id === 'root' && typeof node.component === 'string',
      )
    ) {
      return;
    }
    const tool = this.runs
      .get(responseId)
      ?.tools.find((item) => item.toolCallId === value.toolCallId);
    if (!tool || tool.kind !== 'surface' || tool.state === 'failed') {
      return;
    }
    tool.surfaceId = value.surfaceId;
    tool.surfaceApplied = true;
  }

  handleToolPart(content: AgentContent): void {
    if (!content.responseId) {
      return;
    }
    const run = this.runs.get(content.responseId);
    if (!run) {
      return;
    }
    const toolCallId =
      content.type === ContentType.Component ? content.streaming?.toolCallId : content.messageId;
    const tool = run.tools.find((item) => item.toolCallId === toolCallId);
    if (!tool) {
      return;
    }
    if (content.type === ContentType.Component && !content.hidden) {
      this.#details(tool, content.componentName, content.props);
    }
    if (content.progress?.text.trim()) {
      this.#progress(tool, content.progress, run.state === 'running');
    } else if (content.type === ContentType.Component) {
      const narrate = PROCESS_NARRATIONS[content.componentName];
      if (narrate) {
        tool.commentary = narrate(content.props);
      }
    }
    if (run.state === 'running' && (tool.state === 'preparing' || tool.state === 'running')) {
      tool.narration = tool.commentary;
    }
  }

  #progress(tool: ToolActivity, progress: unknown, running: boolean): void {
    if (!isRecord(progress) || typeof progress.text !== 'string' || !progress.text.trim()) {
      return;
    }
    tool.commentary = authoredNarration(progress.text.trim().slice(0, 500));
    if (running && (tool.state === 'preparing' || tool.state === 'running')) {
      tool.narration = tool.commentary;
    }
  }

  #details(tool: ToolActivity, componentName: unknown, props: Record<string, unknown>): void {
    if (tool.kind === 'image' && tool.state === 'returned') {
      if (typeof props.imageUrl === 'string' && props.imageUrl.length <= 2048) {
        try {
          const url = new URL(props.imageUrl);
          if (url.protocol === 'https:' && !url.username && !url.password) {
            tool.imageUrl = url.href;
          }
        } catch {
          // Invalid optional previews do not change the observed operation outcome.
        }
      }
      return;
    }
    if (
      componentName === 'Surface' &&
      tool.state === 'returned' &&
      typeof props.surfaceId === 'string'
    ) {
      if (tool.surfaceId !== props.surfaceId) {
        tool.surfaceApplied = undefined;
      }
      tool.surfaceId = props.surfaceId;
    }
    if (componentName !== 'DeepResearch' && tool.kind !== 'search') {
      return;
    }
    if (componentName === 'DeepResearch') {
      tool.kind = 'research';
    }
    const subjects = tool.subjects ?? [];
    const searches = Array.isArray(props.searches) ? props.searches : [];
    for (const subject of [...searches, props.currentSearch]) {
      if (typeof subject !== 'string' || !subject.trim()) {
        continue;
      }
      const text = subject.trim().slice(0, 200);
      const previous = subjects.indexOf(text);
      if (previous >= 0) {
        subjects.splice(previous, 1);
      }
      subjects.push(text);
      if (subjects.length > 10) {
        subjects.shift();
      }
    }
    if (subjects.length) {
      tool.subjects = subjects;
    }
    if (!Array.isArray(props.sources)) {
      return;
    }
    const sources: { url: string; title: string }[] = [];
    for (const source of props.sources) {
      if (!isRecord(source) || typeof source.url !== 'string' || source.url.length > 2048) {
        continue;
      }
      try {
        const url = new URL(source.url);
        if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) {
          continue;
        }
        if (sources.some((item) => item.url === url.href)) {
          continue;
        }
        sources.push({
          url: url.href,
          title:
            typeof source.title === 'string' && source.title.trim()
              ? source.title.slice(0, 180)
              : url.hostname,
        });
      } catch {
        continue;
      }
      if (sources.length === 10) {
        break;
      }
    }
    tool.sources = sources;
  }

  #run(responseId: string, parentRunId?: string): RunActivity {
    if (!this.runs.has(responseId)) {
      if (this.runs.size >= MAX_RUNS) {
        const oldest = this.runs.keys().next().value;
        if (oldest !== undefined) {
          this.runs.delete(oldest);
        }
      }
      this.runs.set(responseId, {
        responseId,
        parentRunId,
        state: 'running',
        tools: [],
        truncated: false,
      });
    }
    const run = this.runs.get(responseId);
    if (!run) {
      throw new Error('Activity run was not retained');
    }
    return run;
  }

  #observe(
    responseId: string,
    toolCallId: string,
    state: ActivityState,
    componentName: unknown,
    activityKind?: unknown,
  ): void {
    const run = this.#run(responseId);
    if (run.state !== 'running' && run.state !== 'unconfirmed') {
      return;
    }
    this.responseId ??= responseId;
    const tool = run.tools.find((item) => item.toolCallId === toolCallId);
    if (!tool) {
      if (run.tools.length >= MAX_TOOLS) {
        run.truncated = true;
        return;
      }
      run.tools.push({ toolCallId, state, kind: this.#kind(componentName, activityKind) });
      return;
    }
    const kind = this.#kind(componentName, activityKind);
    if (kind !== 'tool') {
      tool.kind = kind;
    }
    if (tool.state === 'returned' || tool.state === 'failed') {
      return;
    }
    if (
      (state === 'preparing' && tool.state === 'running') ||
      (tool.state === 'unconfirmed' && state !== 'returned' && state !== 'failed')
    ) {
      return;
    }
    tool.state = state;
    if (state === 'returned' || state === 'failed') {
      tool.narration = undefined;
    }
  }

  #kind(componentName: unknown, activityKind: unknown): ToolActivity['kind'] {
    if (componentName === 'Surface') {
      return 'surface';
    }
    if (componentName === 'DeepResearch') {
      return 'research';
    }
    if (componentName === 'MCPToolStatus') {
      return 'service';
    }
    if (
      activityKind === 'search' ||
      activityKind === 'image' ||
      activityKind === 'service' ||
      activityKind === 'computation'
    ) {
      return activityKind;
    }
    return 'tool';
  }

  #settleUnknown(run: RunActivity): void {
    for (const tool of run.tools) {
      if (tool.state === 'preparing' || tool.state === 'running') {
        tool.state = 'unconfirmed';
        tool.narration = undefined;
      }
    }
  }
}
